import { TASK_POSITION_GAP } from './types';

/**
 * Where a dropped row lands.
 *
 * Positions are gapped integers, so a drop between two rows usually only needs
 * to write the row that moved: take the midpoint of its new neighbours. That
 * stays true for about ten drops into the same gap, after which the midpoint
 * has nowhere left to go — 1000 halves to 500, 250, and eventually to a pair
 * of adjacent integers with nothing between them.
 *
 * When that happens the section is renumbered back onto clean gaps. It is the
 * expensive path, so it is the fallback rather than the rule: renumbering on
 * every drop would write every row in a section to move one card.
 *
 * Everything here is a pure function over an ordered list. The caller decides
 * what to do with the result, which is what makes the collision case testable
 * without a database.
 */

export interface Positioned {
  id: string;
  position: number;
}

export type ReorderPlan =
  /** One row moved; nobody else's position changed. */
  | { kind: 'single'; updates: [Positioned] }
  /** The gap ran out, so the whole destination list is renumbered. */
  | { kind: 'reindex'; updates: Positioned[] }
  /** The drop changes nothing — dropping a row onto itself, or where it is. */
  | { kind: 'noop'; updates: [] };

/**
 * Plan a drop of `movingId` into `targetIndex` of `ordered`.
 *
 * `ordered` is the DESTINATION section in display order. For a move within one
 * section it still contains the moving row; it is removed first, so the index
 * means "where the row ends up in the list you can see", not an index into
 * some intermediate array nobody is looking at.
 */
export function planReorder(
  ordered: Positioned[],
  movingId: string,
  targetIndex: number,
): ReorderPlan {
  const from = ordered.findIndex((row) => row.id === movingId);
  const without = from === -1 ? [...ordered] : ordered.filter((row) => row.id !== movingId);

  // Clamp rather than trust: a drop past the last row is a drop at the end,
  // not an error, and the caller counts rows in the DOM.
  const index = Math.max(0, Math.min(targetIndex, without.length));

  // Landing back where it started, in the same list, is not a write. A row
  // arriving from another section has from === -1, which a clamped index can
  // never equal, so this cannot swallow a real cross-section move.
  if (index === from) {
    return { kind: 'noop', updates: [] };
  }

  const before = index > 0 ? without[index - 1] : undefined;
  const after = index < without.length ? without[index] : undefined;

  // An empty destination: the first row starts one gap in, matching how a
  // brand-new section numbers its first task.
  if (!before && !after) {
    return { kind: 'single', updates: [{ id: movingId, position: TASK_POSITION_GAP }] };
  }

  if (!before && after) {
    // Halve toward zero rather than stepping a full gap down: the column has a
    // `position > 0` constraint, so a list that starts at 1000 would otherwise
    // reindex on the very first drop at the top. Halving keeps room for about
    // ten more before it runs out.
    const position = Math.floor(after.position / 2);
    return position >= 1 && position < after.position
      ? { kind: 'single', updates: [{ id: movingId, position }] }
      : reindex(without, movingId, index);
  }

  if (before && !after) {
    return {
      kind: 'single',
      updates: [{ id: movingId, position: before.position + TASK_POSITION_GAP }],
    };
  }

  if (before && after) {
    const midpoint = Math.floor((before.position + after.position) / 2);
    // Strictly between both, or there is no room and the integers have run out.
    return midpoint > before.position && midpoint < after.position
      ? { kind: 'single', updates: [{ id: movingId, position: midpoint }] }
      : reindex(without, movingId, index);
  }

  return { kind: 'noop', updates: [] };
}

/**
 * Renumber the destination list onto clean gaps with the moving row inserted.
 *
 * Every row is returned, including ones whose position happens to be unchanged:
 * the caller writes them as one batch, and filtering here would make the plan
 * depend on the order rows arrived in.
 */
function reindex(without: Positioned[], movingId: string, index: number): ReorderPlan {
  const placed = [
    ...without.slice(0, index),
    { id: movingId, position: 0 },
    ...without.slice(index),
  ];
  return {
    kind: 'reindex',
    updates: placed.map((row, position) => ({
      id: row.id,
      position: (position + 1) * TASK_POSITION_GAP,
    })),
  };
}
